import React from 'react';

import type { UUID } from '@aline/common';
import { getWorkflowAiLifecycleState } from '@aline/common';
import { Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PanelHeader, Spacer } from 'src/components';
import { useGetTaskWithComments } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { TaskPanelBody, TaskPanelSkeleton } from './task-review/TaskPanelBody';
import { TaskPreviewButton } from './task-review/TaskPreviewButton';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflow: 'hidden',
  },
}));

interface TaskDetailsPanelProps {
  taskId: UUID;
  workflowId: string;
}

export const TaskDetailsPanel = React.memo(function TaskDetailsPanel({
  taskId,
  workflowId,
}: TaskDetailsPanelProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const links = useWorkflowDetailLinks(workflowId);
  const { loading, task } = useGetTaskWithComments(taskId);

  if (loading || !task) return <TaskDetailsSkeleton />;

  const aiBuildState = links.workflow ? getWorkflowAiLifecycleState(links.workflow) : 'idle';

  return (
    <div className={classes.root}>
      <PanelHeader
        showBackButton
        text="Task Detail"
        onClickBack={() =>
          navigate(task.parentTask ? links.task(task.parentTask.id) : links.baseUrl())
        }
      >
        <Spacer />
        {task.requesterIsInternal && <TaskPreviewButton task={task} />}
      </PanelHeader>
      <TaskPanelBody
        isAiBuilding={aiBuildState === 'building'}
        task={task}
        workflowId={workflowId}
        onOpenTask={id => navigate(links.task(id))}
      />
    </div>
  );
});

function TaskDetailsSkeleton() {
  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      <PanelHeader text="">
        <Spacer />
        <Skeleton height={32} variant="circular" width={32} />
      </PanelHeader>
      <TaskPanelSkeleton />
    </div>
  );
}
